import i18n from '../i18n/config'
import { loadLanguage } from '../i18n/config'
import { mapBackendToI18nCode } from '../i18n/languages'
import { SETTINGS_KEYS } from '../config/environment'
import { useAppStore } from '../store/useAppStore'
import { clearApplicationCaches } from './auth.service'
import { documentListViewService } from './document-list-view.service'
import { permissionsService } from './permissions.service'
import { settingsService } from './settings.service'
import { toastService } from './toast.service'
import { websocketService } from './websocket.service'
import { openDocumentsService } from './open-documents.service'

let syncInFlight: Promise<void> | null = null
let lastSyncAt = 0
const SYNC_DEBOUNCE_MS = 400

/**
 * When another tab logs in or changes organization, the shared session cookie
 * updates but this tab may still show stale user/org in memory. Re-fetch from
 * the server and refresh UI (full reload if the user identity changed).
 */
class SessionSyncService {
  async syncFromServer(): Promise<void> {
    if (window.location.pathname.startsWith('/login')) {
      return
    }

    const { isAuthenticated } = useAppStore.getState()
    if (isAuthenticated !== true) {
      return
    }

    const now = Date.now()
    if (now - lastSyncAt < SYNC_DEBOUNCE_MS) {
      return
    }
    lastSyncAt = now

    if (syncInFlight) {
      return syncInFlight
    }

    syncInFlight = this.doSync()
    try {
      await syncInFlight
    } finally {
      syncInFlight = null
    }
  }

  private async doSync(): Promise<void> {
    const previousUserId = permissionsService.getCurrentUser()?.id ?? null
    const previousOrgId = settingsService.getCurrentOrganizationId()

    try {
      const uiSettings = await settingsService.initializeSettings(true)

      if (!uiSettings.user?.id) {
        window.location.replace('/login')
        return
      }

      const userChanged =
        previousUserId !== null && previousUserId !== uiSettings.user.id
      const orgChanged =
        previousOrgId !== null &&
        previousOrgId !== (uiSettings.currentOrganizationId ?? null)

      if (userChanged) {
        window.location.reload()
        return
      }

      if (!orgChanged) {
        return
      }

      documentListViewService.markOrganizationSwitch()
      clearApplicationCaches()
      openDocumentsService.setActiveOrganizationId(
        uiSettings.currentOrganizationId ?? null,
      )
      useAppStore.getState().setUiSettings(uiSettings)
      permissionsService.initialize(uiSettings.permissions, uiSettings.user)
      useAppStore.getState().setUser(uiSettings.user)

      const languageCode = settingsService.get(SETTINGS_KEYS.LANGUAGE)
      if (languageCode) {
        const i18nCode = mapBackendToI18nCode(languageCode)
        await loadLanguage(i18nCode)
        localStorage.setItem('app_language', i18nCode)
      }

      await settingsService.updateAppearanceSettings()
      await useAppStore.getState().refreshFeatureFlags()

      websocketService.reconnectForOrgChange()

      toastService.showInfo(i18n.t('session_org_changed_other_tab'))
    } catch (error: unknown) {
      const status = (error as { response?: { status?: number } })?.response?.status
      if (status === 401) {
        window.location.replace('/login')
      }
    }
  }
}

export const sessionSyncService = new SessionSyncService()
